import { Loader, Select, Stack, Text } from '@mantine/core';
import type { QuestionnaireItem, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import type { QuestionnaireFormLoadedState } from '@medplum/react-hooks';
import { useMedplum } from '@medplum/react-hooks';
import { useMemo } from 'react';
import {
  CHART_CONTACT_PICKER_OTHER_CODE,
  buildChartContactPickerOptionValue,
} from '../../lib/chart/coordinationOfCareRoi';
import { contactDisplayLabel, contactPrimaryPhone, useChartContacts } from '../../hooks/useChartContacts';

interface ChartContactPickerFieldProps {
  formState: QuestionnaireFormLoadedState;
  context: QuestionnaireResponseItem[];
  item: QuestionnaireItem;
  responseItem: QuestionnaireResponseItem | undefined;
  patientId: string;
  readOnly?: boolean;
  onPhoneAutoFill?: (phone: string) => void;
}

function buildChoiceAnswer(code: string, display: string): NonNullable<QuestionnaireResponseItem['answer']> {
  return [{ valueCoding: { code, display } }];
}

export function ChartContactPickerField({
  formState,
  context,
  item,
  responseItem,
  patientId,
  readOnly = false,
  onPhoneAutoFill,
}: ChartContactPickerFieldProps): JSX.Element {
  const medplum = useMedplum();
  const { contacts, loading, error } = useChartContacts(medplum, patientId);

  const selectedCode = responseItem?.answer?.[0]?.valueCoding?.code
    ?? responseItem?.answer?.[0]?.valueString
    ?? '';

  const selectData = useMemo(() => {
    const rows = contacts.map((contact) => ({
      value: buildChartContactPickerOptionValue(contact),
      label: contactDisplayLabel(contact),
    }));
    rows.push({
      value: CHART_CONTACT_PICKER_OTHER_CODE,
      label: 'Other (add new contact)',
    });
    return rows;
  }, [contacts]);

  const handleChange = (value: string | null) => {
    if (readOnly || !value) return;
    const label = selectData.find((row) => row.value === value)?.label ?? value;
    formState.onChangeAnswer(context, item, buildChoiceAnswer(value, label));

    if (value !== CHART_CONTACT_PICKER_OTHER_CODE) {
      const contact = contacts.find((c) => buildChartContactPickerOptionValue(c) === value);
      const phone = contact ? contactPrimaryPhone(contact) : undefined;
      if (phone && onPhoneAutoFill) onPhoneAutoFill(phone);
    }
  };

  return (
    <Stack gap="xs">
      {loading ? (
        <Loader size="sm" />
      ) : (
        <>
          {error && <Text size="xs" c="red">{error}</Text>}
          <Select
            data={selectData}
            value={selectedCode || null}
            onChange={handleChange}
            placeholder="Select a contact"
            searchable
            disabled={readOnly}
            nothingFoundMessage="No contacts on file"
          />
        </>
      )}
    </Stack>
  );
}
